import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "../lib/content";
import { useI18n } from "../lib/i18n";
import {
  ArrowLeft,
  ArrowRight,
  ScrollText,
  ShieldCheck,
  Globe,
  Database,
  FileText,
  Building2,
  Trash2,
} from "lucide-react";

const ITEMS = [
  {
    to: "/privacy",
    Icon: ShieldCheck,
    color: "#3B82F6",
    title_ja: "プライバシーポリシー",
    title_en: "Privacy Policy",
    desc_ja: "収集する個人情報・利用目的・第三者提供・保存期間など",
    desc_en: "What data we collect, why, sharing, retention",
    testid: "legal-card-privacy",
  },
  {
    to: "/terms",
    Icon: ScrollText,
    color: "#A855F7",
    title_ja: "利用規約",
    title_en: "Terms of Service",
    desc_ja: "本サービスを利用するにあたっての契約・禁止事項・免責",
    desc_en: "Service contract, prohibited use, disclaimers",
    testid: "legal-card-terms",
  },
  {
    to: "/compliance",
    Icon: Globe,
    color: "#00FF94",
    title_ja: "国際コンプライアンス",
    title_en: "International Compliance",
    desc_ja: "GDPR · CCPA · APPI · PDPA · PIPL · UU PDP の遵守状況",
    desc_en: "GDPR / CCPA / APPI / PDPA / PIPL / UU PDP",
    testid: "legal-card-compliance",
  },
  {
    to: "/data-protection",
    Icon: Database,
    color: "#06B6D4",
    title_ja: "データ保護(完全版)",
    title_en: "Data Protection",
    desc_ja: "国際対応のデータ取扱い詳細・暗号化・アクセス管理",
    desc_en: "International data handling, encryption, access control",
    testid: "legal-card-dataprotection",
  },
  {
    to: "/tokushoho",
    Icon: FileText,
    color: "#F59E0B",
    title_ja: "特定商取引法に基づく表記",
    title_en: "Tokushoho (Japan Specified Commercial Act)",
    desc_ja: "日本国内有料サービスの法定表示事項",
    desc_en: "Mandatory disclosures for paid services in Japan",
    testid: "legal-card-tokushoho",
  },
  {
    to: "/about",
    Icon: Building2,
    color: "#8B5CF6",
    title_ja: "運営会社",
    title_en: "About / Company",
    desc_ja: "運営者情報・お問い合わせ窓口",
    desc_en: "Operator information and contact",
    testid: "legal-card-about",
  },
  {
    to: "/data-request",
    Icon: Trash2,
    color: "#EF4444",
    title_ja: "データ削除・オプトアウト申請",
    title_en: "Data Deletion / Opt-out Request",
    desc_ja: "削除・アクセス・修正・販売停止の各種請求フォーム",
    desc_en: "Submit deletion / access / opt-out requests",
    testid: "legal-card-datarequest",
  },
];

export default function Legal() {
  const { tc } = useContent();
  const { lang } = useI18n();
  const isJA = lang === "ja";

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-12 lg:py-20">
      <Link
        to="/"
        data-testid="legal-back-link"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white mb-10"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
        Back
      </Link>

      <div className="mb-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-3">
          // {isJA ? "法的情報・コンプライアンス" : "LEGAL · COMPLIANCE"}
        </div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-white" style={{ fontWeight: 800 }}>
          {tc("legal.index_title", isJA ? "法的情報" : "Legal Information")}
        </h1>
        <p className="text-sm text-[#94A3B8] mt-4 leading-relaxed max-w-2xl">
          {tc(
            "legal.index_subtitle",
            isJA
              ? "本サービスに関する法的書類・コンプライアンス情報の一覧です。各リンクから詳細をご確認いただけます。"
              : "All legal documents and compliance information for this service. Tap each card for details."
          )}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            className="group block p-6 bg-[#0F1525] border border-[#1E2A3F] hover:border-[#3B82F6]/60 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div
                className="h-10 w-10 rounded-[2px] flex items-center justify-center shrink-0"
                style={{ background: item.color + "22", color: item.color }}
              >
                <item.Icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg tracking-tight text-white" style={{ fontWeight: 700 }}>
                  {isJA ? item.title_ja : item.title_en}
                </div>
                <div className="text-xs text-[#94A3B8] mt-1.5 leading-relaxed">
                  {isJA ? item.desc_ja : item.desc_en}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] group-hover:text-[#3B82F6]">
                  {isJA ? "詳細を見る" : "Read more"}
                  <ArrowRight className="h-3 w-3" strokeWidth={2} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-16 pt-8 border-t border-[#1E2A3F]">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] leading-relaxed">
          {isJA
            ? "// 本サービスは医療診断ではなく、参考情報の提供を目的としています。各国のデータ保護法(GDPR / CCPA / APPI 等)に準拠した運営を行っています。"
            : "// This service is not a medical diagnosis and is for reference information only. We comply with applicable data protection laws (GDPR / CCPA / APPI etc.)."}
        </div>
      </div>
    </div>
  );
}
